import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { firstValueFrom } from 'rxjs';
import { ContactImportResultDto } from 'src/core/models/contact/contact-import-result.dto';
import { ContactImportRowResultDto } from 'src/core/models/contact/contact-import-row-result.dto';
import { ContactImportService } from 'src/core/services/contact-import/contact-import.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';

/** Backend error code (see `AgenticCXDomainErrorCodes.CrmContact.ImportResultExpired`). */
const EXPIRED_ERROR_CODE = 'AgenticCX:Contact:ImportResultExpired';

/**
 * Detailed outcome of a CSV contact import (counters + the failed rows). The import itself is
 * asynchronous, so this is no longer shown right after the upload: it is opened from the "View" action of
 * the `ContactImportCompletedNotification` row, which supplies the `importId` the result is loaded by.
 */
@Component({
  selector: 'app-contact-import-result-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, LocalizationModule, DialogModule],
  templateUrl: './contact-import-result-dialog.component.html',
  styleUrl: './contact-import-result-dialog.component.scss',
})
export class ContactImportResultDialogComponent implements OnChanges {
  private readonly importService = inject(ContactImportService);
  private readonly toaster = inject(ToasterService);
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Import run to show; the result is (re)loaded whenever the dialog is opened for a new id. */
  @Input() importId: string | null = null;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  result: ContactImportResultDto | null = null;
  loading = false;

  /** Set when the server reports the result is past its retention window; rendered instead of the table. */
  expiredMessage: string | null = null;

  private loadedImportId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible'] && !changes['importId']) {
      return;
    }

    if (this.visible && this.importId && this.importId !== this.loadedImportId) {
      void this.load(this.importId);
    }
  }

  get failedRows(): ContactImportRowResultDto[] {
    return this.result?.rows?.filter(row => !row.isSuccess) ?? [];
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private async load(importId: string): Promise<void> {
    this.loading = true;
    this.result = null;
    this.expiredMessage = null;
    this.cdr.markForCheck();

    try {
      this.result = await firstValueFrom(this.importService.getImportResult(importId));
      this.loadedImportId = importId;
    } catch (error) {
      const expired = this.extractExpiredMessage(error);
      if (expired) {
        // Expected for an old notification: the result was swept, the notification outlived it. Stays on
        // screen as an explanation instead of closing the dialog on the user.
        this.expiredMessage = expired;
        this.loadedImportId = importId;
      } else {
        this.toaster.error('::UnexpectedErrorMessage');
        this.onHide();
      }
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Returns the server's already-localized "result expired" message, or `null` when the failure was
   * something else (network, server fault) and should be reported as an error instead.
   */
  private extractExpiredMessage(error: unknown): string | null {
    const remote = (error as { error?: { error?: { code?: string; message?: string } } } | undefined)
      ?.error?.error;

    return remote?.code === EXPIRED_ERROR_CODE
      ? remote.message || this.localization.instant('Contact::CsvImportResultNotFound')
      : null;
  }
}
